import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { authSignupSchema, authLoginSchema } from '@/lib/validation';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import gibletNoEyes from "@/assets/giblet-no-eyes.png";
import ispaiText from "@/assets/ispai-text.png";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Refs for Giblet eye tracking
  const svgRef = useRef<SVGSVGElement>(null);
  const leftEyeRef = useRef<SVGCircleElement>(null);
  const rightEyeRef = useRef<SVGCircleElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const displayNameInputRef = useRef<HTMLInputElement>(null);
  const signInButtonRef = useRef<HTMLButtonElement>(null);
  const switchModeButtonRef = useRef<HTMLButtonElement>(null);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoverTarget, setHoverTarget] = useState<string | null>(null);

  // Eye socket positions
  const leftEyeBaseX = 109;
  const leftEyeBaseY = 45;
  const rightEyeBaseX = 189;
  const rightEyeBaseY = 45;
  const eyeSize = 36;
  const pupilSize = 25;
  const maxOffset = 12;
  const movementDivisor = 18;

  // Check for auto logout reason on mount
  useEffect(() => {
    const logoutReason = localStorage.getItem('auto_logout_reason');
    if (logoutReason === 'inactivity') {
      toast({
        title: "Session Expired",
        description: "You were logged out due to inactivity. Your work has been saved.",
        variant: "default"
      });
      localStorage.removeItem('auto_logout_reason');
    }
  }, [toast]);

  // Track mouse movement
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Update eye positions based on mouse or hover target
  useEffect(() => {
    if (!svgRef.current || !leftEyeRef.current || !rightEyeRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    let targetX = mousePosition.x;
    let targetY = mousePosition.y;

    // Override with hover target if set
    if (hoverTarget) {
      let targetRect: DOMRect | undefined;
      switch (hoverTarget) {
        case 'email':
          targetRect = emailInputRef.current?.getBoundingClientRect();
          break;
        case 'password':
          targetRect = passwordInputRef.current?.getBoundingClientRect();
          break;
        case 'displayName':
          targetRect = displayNameInputRef.current?.getBoundingClientRect();
          break;
        case 'submitButton':
          targetRect = signInButtonRef.current?.getBoundingClientRect();
          break;
        case 'switchMode':
          targetRect = switchModeButtonRef.current?.getBoundingClientRect();
          break;
      }
      if (targetRect) {
        targetX = targetRect.left + targetRect.width / 2;
        targetY = targetRect.top + targetRect.height / 2;
      }
    }

    const calculateEyePosition = (eyeBaseX: number, eyeBaseY: number) => {
      const eyeCenterX = svgRect.left + (eyeBaseX + eyeSize / 2) * (svgRect.width / 334);
      const eyeCenterY = svgRect.top + (eyeBaseY + eyeSize / 2) * (svgRect.height / 206);

      const deltaX = targetX - eyeCenterX;
      const deltaY = targetY - eyeCenterY;

      const offsetX = Math.max(-maxOffset, Math.min(maxOffset, deltaX / movementDivisor));
      const offsetY = Math.max(-maxOffset, Math.min(maxOffset, deltaY / movementDivisor));

      return { offsetX, offsetY };
    };

    const leftEyePosition = calculateEyePosition(leftEyeBaseX, leftEyeBaseY);
    const rightEyePosition = calculateEyePosition(rightEyeBaseX, rightEyeBaseY);

    leftEyeRef.current.setAttribute('cx', String(leftEyeBaseX + eyeSize / 2 + leftEyePosition.offsetX));
    leftEyeRef.current.setAttribute('cy', String(leftEyeBaseY + eyeSize / 2 + leftEyePosition.offsetY));

    rightEyeRef.current.setAttribute('cx', String(rightEyeBaseX + eyeSize / 2 + rightEyePosition.offsetX));
    rightEyeRef.current.setAttribute('cy', String(rightEyeBaseY + eyeSize / 2 + rightEyePosition.offsetY));
  }, [mousePosition, hoverTarget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input with Zod
    try {
      if (isLogin) {
        const validation = authLoginSchema.safeParse({ email, password });
        if (!validation.success) {
          const firstError = validation.error.issues[0];
          toast({
            title: "Validation Error",
            description: firstError.message,
            variant: "destructive"
          });
          return;
        }
      } else {
        const validation = authSignupSchema.safeParse({ email, password, displayName });
        if (!validation.success) {
          const firstError = validation.error.issues[0];
          toast({
            title: "Validation Error",
            description: firstError.message,
            variant: "destructive"
          });
          return;
        }
      }
    } catch (validationError: any) {
      toast({
        title: "Validation Error",
        description: validationError.message || "Invalid input",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await login(email, password);
        if (error) {
          toast({
            title: "Login failed",
            description: error.message,
            variant: "destructive"
          });
          setLoading(false);
        } else {
          // Check if user needs to change password or redirect to profile
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('must_change_password, username')
              .eq('id', user.id)
              .maybeSingle();

            if (profile?.must_change_password) {
              toast({
                title: "Welcome!",
                description: "Please change your temporary password.",
              });
              setLoading(false);
              navigate('/change-password');
              return;
            }

            // Check for intended redirect path first
            const redirectPath = localStorage.getItem('auth_redirect_path');

            if (redirectPath) {
              // Clear the saved path
              localStorage.removeItem('auth_redirect_path');
              
              toast({
                title: "Welcome back!",
                description: "Returning to where you left off..."
              });
              
              setLoading(false);
              navigate(redirectPath, { replace: true });
              return;
            }

            toast({
              title: "Welcome back!",
              description: "You have successfully logged in"
            });

            setLoading(false);

            // All logged in users go to creators page
            navigate('/creators');
          } else {
            setLoading(false);
            navigate('/creators');
          }
        }
      } else {
        const { error } = await signup(email, password, displayName);
        if (error) {
          toast({
            title: "Signup failed",
            description: error.message,
            variant: "destructive"
          });
          setLoading(false);
        } else {
          toast({
            title: "Account created!",
            description: "Welcome to iSPAI"
          });
          setLoading(false);
          navigate('/create-myspai-profile');
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex flex-col items-center mb-4 space-y-2">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <svg ref={svgRef} width="167" height="103" viewBox="0 0 334 206" className="animate-fade-in">
                <image href={gibletNoEyes} width="334" height="206" x="0" y="0" />
                <circle 
                  ref={leftEyeRef}
                  cx={leftEyeBaseX + eyeSize / 2} 
                  cy={leftEyeBaseY + eyeSize / 2} 
                  r={pupilSize / 2} 
                  fill="black"
                  style={{ transition: 'all 0.15s ease-out' }}
                />
                <circle 
                  ref={rightEyeRef}
                  cx={rightEyeBaseX + eyeSize / 2} 
                  cy={rightEyeBaseY + eyeSize / 2} 
                  r={pupilSize / 2} 
                  fill="black"
                  style={{ transition: 'all 0.15s ease-out' }}
                />
              </svg>
            </motion.div>

            <motion.img
              src={ispaiText}
              alt="iSPAI"
              className="h-10 object-contain"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </div>
          <CardTitle className="text-2xl text-center">
            {isLogin ? 'Welcome back' : 'Create an account'}
          </CardTitle>
          <CardDescription className="text-center">
            {isLogin 
              ? 'Enter your credentials to access your account' 
              : 'Enter your information to get started'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  ref={displayNameInputRef}
                  id="displayName"
                  type="text"
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onFocus={() => setHoverTarget('displayName')}
                  onBlur={() => setHoverTarget(null)}
                  required={!isLogin}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                ref={emailInputRef}
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setHoverTarget('email')}
                onBlur={() => setHoverTarget(null)}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <Input
                ref={passwordInputRef}
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setHoverTarget('password')}
                onBlur={() => setHoverTarget(null)}
                required
              />
            </div>
            {isLogin && (
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember-me" 
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                />
                <Label 
                  htmlFor="remember-me" 
                  className="text-sm font-normal cursor-pointer"
                >
                  Remember me for 30 days
                </Label>
              </div>
            )}
            <Button 
              ref={signInButtonRef}
              type="submit"
              variant="active" 
              className="w-full" 
              disabled={loading}
              onMouseEnter={() => setHoverTarget('submitButton')}
              onMouseLeave={() => setHoverTarget(null)}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <button
              ref={switchModeButtonRef}
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary hover:underline"
              onMouseEnter={() => setHoverTarget('switchMode')}
              onMouseLeave={() => setHoverTarget(null)}
            >
              {isLogin 
                ? "Don't have an account? Sign up" 
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </CardContent>
      </Card>
      
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen}
      />
    </div>
  );
};

export default Auth;
